import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { VOICEFLOW } from "../lib/voiceflowConfig";
import { specialistExtension } from "../lib/specialists";
import SpeakerHeader from "./SpeakerHeader";
import { attachResponseAvatars } from "../lib/responseAvatars";
import { saveConversation } from "../lib/api";
import ReportOffer from "./ReportOffer";
import AnswerCardDialog from "./AnswerCardDialog";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";

let bundlePromise = null;
const loadBundle = () => {
  if (window.voiceflow?.chat) return Promise.resolve();
  if (!bundlePromise) {
    bundlePromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = VOICEFLOW.bundleSrc;
      s.type = "text/javascript";
      s.onload = resolve;
      s.onerror = reject;
      document.body.appendChild(s);
    });
  }
  return bundlePromise;
};

export default function VerdantPanel({ context, sessionId, onClose }) {
  const target = useRef(null);
  const [state, setState] = useState(VOICEFLOW.projectID ? "loading" : "pending");
  const [ready, setReady] = useState(0);
  const [open, setOpen] = useState(false);
  const [answer, setAnswer] = useState(null);
  const saveTimer = useRef(null);

  const onMessages = (msgs) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveConversation(sessionId, context.case_id, msgs)
        .then((r) => { if (r.report_ready) setReady((n) => n + 1); })
        .catch(() => {});
    }, 2500);
  };

  useEffect(() => {
    if (!VOICEFLOW.projectID) return undefined;
    let alive = true;
    let detach = null;
    loadBundle()
      .then(() => {
        if (!alive) return;
        window.voiceflow.chat.load({
          verify: { projectID: VOICEFLOW.projectID },
          url: VOICEFLOW.url,
          ...(VOICEFLOW.versionID ? { versionID: VOICEFLOW.versionID } : {}),
          ...(VOICEFLOW.voiceUrl ? { voice: { url: VOICEFLOW.voiceUrl } } : {}),
          userID: context.case_id,
          render: { mode: "embedded", target: target.current },
          autostart: true,
          assistant: { persistence: "sessionStorage", extensions: [specialistExtension] },
          launch: { event: { type: "launch", payload: { greenflow_case: context, greenflow_case_text: context.case_text } } },
        });
        detach = attachResponseAvatars(target.current, context.case_id, sessionId ? onMessages : null, setAnswer);
        setState("ready");
      })
      .catch(() => alive && setState("error"));
    return () => {
      alive = false;
      detach?.();
      window.voiceflow?.chat?.destroy?.();
    };
  }, [context.case_id]); // eslint-disable-line

  return createPortal(
    <div data-testid="verdant-panel" className="fixed inset-0 z-[60] bg-[#f8faf8] flex flex-col">
      <SpeakerHeader caseId={context.case_id} onClose={onClose} />
      {ready > 0 && (
        <button data-testid="report-offer-strip" onClick={() => setOpen(true)}
          className="w-full bg-[#F3F8F5] border-b border-emerald-200 px-4 py-2 text-left text-xs sm:text-sm font-bold text-[#14532D] hover:bg-emerald-50 transition-colors">
          KEEP YOUR COMPLETE ASK DR. VERDANT REPORT <span className="text-sky-700">&rarr; See options</span>
        </button>
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" data-testid="report-offer-sheet" className="z-[70] max-h-[90dvh] overflow-y-auto pt-12">
          <SheetTitle className="sr-only">Report options</SheetTitle>
          <ReportOffer sessionId={sessionId} refreshKey={ready} />
        </SheetContent>
      </Sheet>
      <AnswerCardDialog answer={answer} sessionId={sessionId} onClose={() => setAnswer(null)} />
      <div ref={target} data-testid="verdant-chat-target" className="relative flex-1 min-h-0 w-full" style={{ height: "calc(100dvh - 72px)" }} />
      {state !== "ready" && (
        <div data-testid="verdant-status" className="absolute inset-x-0 top-1/3 px-6 text-center text-sm text-slate-600">
          {state === "pending" && "Ask Dr. Verdant is being connected. Your case and photos are ready and will be shared automatically."}
          {state === "loading" && "Connecting you with Dr. Verdant\u2026"}
          {state === "error" && "We couldn't open Ask Dr. Verdant right now. Please try again in a moment."}
        </div>
      )}
    </div>,
    document.body
  );
}
